import { useState } from "react";
import { Upload, FileText, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import BlueCubeSpinner from "./BlueCubeSpinner";
import { documentSizeError, DOCUMENT_SIZE_LABEL, uploadDocumentChunks } from "../utils/documentUpload";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DragDropUpload = ({ onUploadComplete }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [failedFiles, setFailedFiles] = useState([]);
  const busy = uploadingFiles.some(file => file.status === 'uploading');

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    await uploadFiles(files);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    await uploadFiles(files);
  };

  const PARALLEL_UPLOADS = 3; // Number of files uploaded concurrently
  const MAX_FILES_PER_BATCH = 100;

  // Per-file upload (returns { ok: true } or { ok: false, reason, file })
  const uploadOneFile = async (file, token, forceDup = false) => {
    try {
      const sizeError = documentSizeError(file);
      if (sizeError) throw new Error(sizeError);
      const itemData = {
        title: file.name.replace(/\.[^/.]+$/, "").replace(/[_-]/g, ' '),
        authors: [],
        abstract: null,
        year: new Date().getFullYear(),
        language: 'en',
        tags: [],
        access_level: 'public',
        category: 'new',
      };

      const itemResponse = await axios.post(`${API}/items`, itemData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const itemId = itemResponse.data.id;

      const dupQs = forceDup ? '?allow_duplicate=true' : '';
      await uploadDocumentChunks({ file, token, url: `${API}/items/${itemId}/upload-chunk${dupQs}`,
        onProgress: (progress) => setUploadingFiles(prev => prev.map(f =>
          f.name === file.name ? { ...f, progress } : f)),
      });

      setUploadingFiles(prev =>
        prev.map(f => f.name === file.name ? { ...f, status: 'success' } : f)
      );
      toast.success(`✅ ${file.name}${forceDup ? ' (forced)' : ''}`);
      return { ok: true, file };
    } catch (error) {
      console.error('Upload error for', file.name, ':', error);
      let reason = "Upload failed";
      const detail = error.response?.data?.detail;
      const status = error.response?.status;

      // Detail may be a string (legacy) or a structured object
      // ({ message, dup_kind, blocking_item_id, blocking_has_file, ... }).
      let detailMessage = null;
      let blockingHasFile = null;
      if (typeof detail === 'string') {
        detailMessage = detail;
      } else if (detail && typeof detail === 'object') {
        detailMessage = detail.message || JSON.stringify(detail);
        if (typeof detail.blocking_has_file === 'boolean') {
          blockingHasFile = detail.blocking_has_file;
        }
      }

      if (status === 409) {
        if (detailMessage && detailMessage.trim().length > 0) {
          reason = detailMessage.length > 200 ? detailMessage.substring(0, 200) + "…" : detailMessage;
        } else {
          reason = "Duplicate file";
        }
      }
      else if (status === 400 && detail && typeof detail === 'object' && detail.error_kind === 'conversion_failed') {
        // Auto-convert-to-PDF failed — show a clear, actionable message
        reason = detailMessage || "Could not convert this file to PDF. Please convert it manually and re-upload as PDF.";
        if (reason.length > 240) reason = reason.substring(0, 240) + "…";
      }
      else if (status === 413) reason = `Maximum ${DOCUMENT_SIZE_LABEL}`;
      else if (status === 401 || status === 403) reason = "Not authorized";
      else if (typeof detail === 'string') reason = detail.length > 60 ? detail.substring(0, 60) + "..." : detail;
      else if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) reason = "Timed out — try again";
      else if (error.message?.includes("Network Error")) reason = "Network error — try again";
      else if (!error.response && error.message) reason = error.message;

      // Show Force-upload button only when:
      //   - the 409 is a dup
      //   - AND the blocking item is a ghost (no file) — otherwise forcing
      //     would create a genuine duplicate, which the user probably doesn't want.
      const showForce = status === 409 && blockingHasFile === false;

      setUploadingFiles(prev =>
        prev.map(f => f.name === file.name ? { ...f, status: 'error', reason, isDup: status === 409, showForce } : f)
      );
      toast.error(`${file.name}: ${reason}`);
      return { ok: false, file, reason, isDup: status === 409, showForce };
    }
  };

  const forceUploadOne = async (file) => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      toast.error('Please login first');
      return;
    }
    setUploadingFiles(prev =>
      prev.map(f => f.name === file.name ? { ...f, status: 'uploading', reason: undefined } : f)
    );
    setFailedFiles(prev => prev.filter(f => f.name !== file.name));
    const result = await uploadOneFile(file, token, true);
    if (result.ok) {
      if (onUploadComplete) onUploadComplete();
    } else {
      setFailedFiles(prev => [...prev, file]);
    }
  };

  const uploadFiles = async (files) => {
    if (busy) return;
    const token = localStorage.getItem('access_token');

    if (!token) {
      toast.error('Please login first');
      return;
    }

    // Filter valid files. Backend auto-converts non-PDFs to PDF on upload,
    // so we accept Office docs, images, ebooks, and web content too.
    const validFiles = files.filter(file => {
      const sizeError = documentSizeError(file);
      if (sizeError) {
        toast.error(`${file.name}: ${sizeError}`);
        return false;
      }
      const validTypes = [
        '.pdf',
        // Office documents (auto-converted via LibreOffice)
        '.docx', '.doc', '.odt', '.rtf',
        '.pptx', '.ppt', '.odp',
        '.xlsx', '.xls', '.ods',
        '.txt',
        // Images (auto-converted via img2pdf)
        '.jpg', '.jpeg', '.png', '.tiff', '.tif', '.bmp', '.webp', '.gif',
        // Web / ebook (auto-converted via WeasyPrint / ebooklib)
        '.html', '.htm', '.epub',
      ];
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return validTypes.includes(ext);
    });

    if (validFiles.length === 0) {
      if (files.some(file => documentSizeError(file))) return;
      toast.error('No valid files found. Supported: PDF, DOCX, PPTX, XLSX, ODT, RTF, TXT, HTML, EPUB, JPG, PNG, TIFF, GIF, WEBP (non-PDFs are auto-converted to PDF).');
      return;
    }

    if (validFiles.length > MAX_FILES_PER_BATCH) {
      toast.error(`Maximum ${MAX_FILES_PER_BATCH} files at once`);
      return;
    }

    setFailedFiles([]);
    setUploadingFiles(validFiles.map(f => ({ name: f.name, status: 'uploading', progress: 0 })));
    const parallelUploads = validFiles.some(f => f.size > 25 * 1024 * 1024) ? 1 : PARALLEL_UPLOADS;
    toast.info(`Uploading ${validFiles.length} file(s) — ${parallelUploads} at a time`);

    // Concurrency pool: keep PARALLEL_UPLOADS workers pulling from a shared queue
    const queue = [...validFiles];
    const failed = [];

    const worker = async () => {
      while (queue.length > 0) {
        const next = queue.shift();
        if (!next) return;
        const result = await uploadOneFile(next, token);
        if (!result.ok) failed.push(next);
      }
    };

    const workerCount = Math.min(parallelUploads, validFiles.length);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));

    setFailedFiles(failed);
    setTimeout(() => {
      if (failed.length === 0) setUploadingFiles([]);
      if (onUploadComplete) onUploadComplete();
    }, 3000);
  };

  const retryFailed = async (force = false) => {
    if (failedFiles.length === 0) return;
    const filesToRetry = [...failedFiles];
    setFailedFiles([]);
    if (force) {
      const token = localStorage.getItem('access_token');
      if (!token) { toast.error('Please login first'); return; }
      toast.info(`Force-uploading ${filesToRetry.length} file(s) — duplicate checks bypassed`);
      // Re-mark them as "uploading" in the UI list
      setUploadingFiles(prev => prev.map(f =>
        filesToRetry.find(rf => rf.name === f.name)
          ? { ...f, status: 'uploading', reason: undefined, isDup: false }
          : f
      ));
      const stillFailed = [];
      const queue = [...filesToRetry];
      const worker = async () => {
        while (queue.length > 0) {
          const next = queue.shift();
          if (!next) return;
          const result = await uploadOneFile(next, token, true);
          if (!result.ok) stillFailed.push(next);
        }
      };
      const workerCount = Math.min(PARALLEL_UPLOADS, filesToRetry.length);
      await Promise.all(Array.from({ length: workerCount }, () => worker()));
      setFailedFiles(stillFailed);
      if (stillFailed.length === 0 && onUploadComplete) onUploadComplete();
    } else {
      await uploadFiles(filesToRetry);
    }
  };

  return (
    <div className="mb-6">
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center transition-all
          ${isDragging 
            ? 'border-orange-500 bg-orange-50' 
            : 'border-slate-300 bg-slate-50 hover:border-slate-400'}
        `}
        data-testid="drag-drop-zone"
      >
        {uploadingFiles.some((f) => f.status === 'uploading') && (
          <div className="absolute top-3 right-3 flex items-center text-xs text-blue-700">
            <span>Uploading</span>
            <BlueCubeSpinner />
          </div>
        )}
        <input
          type="file"
          id="file-upload"
          multiple
          accept=".pdf,.docx,.doc,.odt,.rtf,.pptx,.ppt,.odp,.xlsx,.xls,.ods,.txt,.html,.htm,.epub,.jpg,.jpeg,.png,.tiff,.tif,.bmp,.webp,.gif"
          onChange={handleFileSelect}
          data-testid="bulk-upload-file-input"
          disabled={busy}
          className="hidden"
        />
        
        <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-orange-500' : 'text-slate-400'}`} />
        
        <h3 className="text-lg font-semibold mb-2 text-slate-900">
          {isDragging ? 'Drop files here!' : 'Drag & Drop Files Here'}
        </h3>
        
        <p className="text-sm text-slate-600 mb-4">
          Or click to browse your computer
        </p>
        
        <label htmlFor="file-upload" data-testid="bulk-upload-choose-files">
          <span className="inline-block px-6 py-2 bg-orange-500 text-white rounded-md cursor-pointer hover:bg-orange-600 transition-colors">
            Choose Files
          </span>
        </label>
        
        <p className="text-xs text-slate-500 mt-4" data-testid="bulk-upload-limits">
          PDF, Office, TXT, HTML, EPUB, images — non-PDFs are auto-converted • Max 100 files • {DOCUMENT_SIZE_LABEL}
        </p>
      </div>

      {/* Upload Progress */}
      {uploadingFiles.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="upload-progress">
          {failedFiles.length > 0 && (
            <div className="flex gap-2">
              <button
                onClick={() => retryFailed(false)}
                className="flex-1 py-2 px-4 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-md transition-colors"
                data-testid="retry-failed-btn"
              >
                Retry {failedFiles.length} failed file(s)
              </button>
              {failedFiles.some((f) => {
                const u = uploadingFiles.find((x) => x.name === f.name);
                return u && u.showForce;
              }) && (
                <button
                  onClick={() => {
                    const ghostFiles = failedFiles.filter((f) => {
                      const u = uploadingFiles.find((x) => x.name === f.name);
                      return u && u.showForce;
                    });
                    if (!window.confirm(`Force-upload ${ghostFiles.length} file(s) where the blocker is a ghost (no file attached)? This bypasses dup checks for these only — real duplicates are skipped.`)) return;
                    // Re-queue only the ghost-blocked files
                    setFailedFiles((prev) => prev.filter((f) => !ghostFiles.some((g) => g.name === f.name)));
                    const token = localStorage.getItem('access_token');
                    if (!token) { toast.error('Please login first'); return; }
                    setUploadingFiles((prev) => prev.map((f) =>
                      ghostFiles.find((g) => g.name === f.name)
                        ? { ...f, status: 'uploading', reason: undefined, isDup: false, showForce: false }
                        : f
                    ));
                    toast.info(`Force-uploading ${ghostFiles.length} ghost-blocked file(s)`);
                    (async () => {
                      const stillFailed = [];
                      const queue = [...ghostFiles];
                      const worker = async () => {
                        while (queue.length > 0) {
                          const next = queue.shift();
                          if (!next) return;
                          const result = await uploadOneFile(next, token, true);
                          if (!result.ok) stillFailed.push(next);
                        }
                      };
                      const workerCount = Math.min(PARALLEL_UPLOADS, ghostFiles.length);
                      await Promise.all(Array.from({ length: workerCount }, () => worker()));
                      setFailedFiles((prev) => [...prev, ...stillFailed]);
                      if (stillFailed.length === 0 && onUploadComplete) onUploadComplete();
                    })();
                  }}
                  className="flex-1 py-2 px-4 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md transition-colors"
                  data-testid="force-upload-all-btn"
                  title="Force-upload only the files whose blocker is a ghost (item with no file attached)"
                >
                  Force upload {failedFiles.filter((f) => {
                    const u = uploadingFiles.find((x) => x.name === f.name);
                    return u && u.showForce;
                  }).length} ghost-blocked file(s)
                </button>
              )}
            </div>
          )}
          {uploadingFiles.map((file, idx) => (
            <div
              key={idx}
              className="flex flex-wrap gap-3 items-center justify-between p-3 bg-white border border-slate-200 rounded-md"
              data-testid={`bulk-upload-row-${idx}`}
            >
              <div className="flex items-center space-x-3 min-w-0 flex-1">
                <FileText className="w-5 h-5 text-slate-400 shrink-0" />
                <span className="text-sm text-slate-700 break-all" data-testid={`bulk-upload-filename-${idx}`}>{file.name}</span>
              </div>
              <div>
                {file.status === 'uploading' && (
                  <div className="w-32" data-testid={`bulk-upload-progress-${idx}`}>
                    <span className="text-xs text-blue-600">{file.progress >= 99 ? 'Saving…' : `Uploading ${file.progress || 0}%`}</span>
                    <progress data-testid={`bulk-upload-progress-bar-${idx}`} className="w-full h-1.5 accent-orange-500" max="100" value={file.progress || 0} aria-label={`Upload progress for ${file.name}`} />
                  </div>
                )}
                {file.status === 'success' && (
                  <span className="text-xs text-green-600" data-testid={`bulk-upload-success-${idx}`}>Done</span>
                )}
                {file.status === 'error' && (
                  <div className="flex items-start space-x-2 max-w-md">
                    <span className="text-xs text-red-600 text-right break-words" data-testid={`bulk-upload-error-${idx}`} title={file.reason}>{file.reason || "Failed"}</span>
                    {file.showForce && (
                      <button
                        type="button"
                        onClick={() => {
                          const originalFile = failedFiles.find(f => f.name === file.name);
                          if (originalFile) forceUploadOne(originalFile);
                          else toast.error("Original file reference lost — pick the file again");
                        }}
                        className="text-[10px] font-semibold px-2 py-0.5 rounded bg-red-100 text-red-700 hover:bg-red-200 border border-red-300 transition-colors flex-shrink-0"
                        data-testid={`force-upload-btn-${idx}`}
                        title="The existing item is a ghost (no file). Click to skip dup check and attach this file."
                      >
                        Force upload
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DragDropUpload;
